import { createContext, ReactElement, useState, useEffect, useCallback } from "react";
import { Vendor } from "types/vendor";
import axios from "axios";
import type { HeritageConnectPhase } from "../views/vendors/utils/heritageConnectionStatus";


interface VendorContextProps {
  currentVendor: Vendor | null;
  handleUpdateVendor: (newVendor: Vendor) => void;
  updateMode?: boolean;
  handleUpdateVendorData?: (vendorKey:string, value:string) => void;
  updater: (vendor:any) => void;
  toggleUpdateModeButton: (editMode: boolean, refetch: any, vendorOverride?: Vendor, skipToggleUpdateMode?: boolean) => void;
  deleteVendor: (refetch: any) => Promise<void>;
  hideVendorDetails: () => void;
  vendorDetailsHidden: boolean;
  isVendorEditModalOpen: boolean;
  heritageCredentialsFocusOnOpen: boolean;
  openVendorEditModal: (options?: { focusHeritageCredentials?: boolean }) => void;
  closeVendorEditModal: () => void;
  clearHeritageCredentialsFocus: () => void;
  heritageConnectPhase: HeritageConnectPhase;
  heritageConnectErrorMessage: string | null;
  reportHeritageConnectStart: () => void;
  reportHeritageConnectSuccess: () => void;
  reportHeritageConnectFailure: (message: string) => void;
}

export const VendorContext = createContext<VendorContextProps>(
  {} as VendorContextProps
);

interface IProps {
  children: ReactElement;
}

export const VendorProvider = ({ children }: IProps) => {
  const [currentVendor, setCurrentVendor] = useState<Vendor | null>(null);
  const [updateMode, setUpdateMode] = useState(false);

  const [vendorDetailsHidden, setVendorDetailsHidden] = useState(false);
  const [isVendorEditModalOpen, setIsVendorEditModalOpen] = useState(false);
  const [heritageCredentialsFocusOnOpen, setHeritageCredentialsFocusOnOpen] = useState(false);
  const [heritageConnectPhase, setHeritageConnectPhase] = useState<HeritageConnectPhase>("idle");
  const [heritageConnectErrorMessage, setHeritageConnectErrorMessage] = useState<string | null>(null);

  const resetHeritageConnectUi = useCallback(() => {
    setHeritageConnectPhase("idle");
    setHeritageConnectErrorMessage(null);
  }, []);

  const reportHeritageConnectStart = useCallback(() => {
    setHeritageConnectPhase("connecting");
    setHeritageConnectErrorMessage(null);
  }, []);

  const reportHeritageConnectSuccess = useCallback(() => {
    setHeritageConnectPhase("idle");
    setHeritageConnectErrorMessage(null);
  }, []);

  const reportHeritageConnectFailure = useCallback((message: string) => {
    setHeritageConnectPhase("failed");
    setHeritageConnectErrorMessage(message);
  }, []);

  const hideVendorDetails = () => setVendorDetailsHidden(!vendorDetailsHidden);

  const openVendorEditModal = (options?: { focusHeritageCredentials?: boolean }) => {
    setHeritageCredentialsFocusOnOpen(Boolean(options?.focusHeritageCredentials));
    setIsVendorEditModalOpen(true);
  };
  const closeVendorEditModal = () => {
    setIsVendorEditModalOpen(false);
    setHeritageCredentialsFocusOnOpen(false);
  };
  const clearHeritageCredentialsFocus = useCallback(
    () => setHeritageCredentialsFocusOnOpen(false),
    []
  );

  useEffect(() => {
    setIsVendorEditModalOpen(false);
    resetHeritageConnectUi();
  }, [currentVendor?.vendor_id, resetHeritageConnectUi]);

  const handleUpdateVendor = (newVendor: Vendor) => {
    setCurrentVendor(newVendor);
    setUpdateMode(false)

    return;
  };

  const updater = (vendor: any): Promise<void> => {
    setCurrentVendor(vendor);
    return Promise.resolve();
  };
  

  const toggleUpdateModeButton = async (
    editMode: boolean,
    refetch: any,
    vendorOverride?: Vendor,
    skipToggleUpdateMode: boolean = false
  ): Promise<void> => {
    const vendorToUse = vendorOverride || currentVendor;
  
    if (editMode && vendorToUse) {
      await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/vendors/updateVendor`, {
        vendorId: vendorToUse.vendor_id,
        data: vendorToUse,
      });
  
      if (refetch) {
        await refetch();
        setCurrentVendor(vendorToUse);
      }
    }
  
    if (!skipToggleUpdateMode) {
      setUpdateMode(!updateMode);
    }
  };

  const deleteVendor = async (refetch: any) => {
    await axios.post(
      `${process.env.REACT_APP_SERVER_URL}/api/v1/company/vendors/deleteVendor`,
      {
        vendorId: currentVendor!.vendor_id,
      }
    );
    if (refetch) {
      await refetch();
      setCurrentVendor(null);
    }
  };

  return (
    <VendorContext.Provider
      value={{
        currentVendor,
        handleUpdateVendor,
        updateMode,
        updater,
        toggleUpdateModeButton,
        deleteVendor,
        hideVendorDetails,
        vendorDetailsHidden,
        isVendorEditModalOpen,
        heritageCredentialsFocusOnOpen,
        openVendorEditModal,
        closeVendorEditModal,
        clearHeritageCredentialsFocus,
        heritageConnectPhase,
        heritageConnectErrorMessage,
        reportHeritageConnectStart,
        reportHeritageConnectSuccess,
        reportHeritageConnectFailure,
      }}
    >
      {children}
    </VendorContext.Provider>
  );
};
